import React, { useEffect } from "react";
import S from "./Columns.styled";

import TitleParts from "@/components/global-components/TitleParts";
import { Flex, motionAnimation } from "@/styles/global";
import { H3, Body, DisplayHeadingS } from "@/styles/typography";
import {
  ColorOptions,
  IWPImage,
  IWPLink,
  IWPTitleParts,
  IWPVideo,
} from "@/src/types";
import { Image, TextButton, Wysiwyg } from "@/styles/components";
import { isLight } from "@/src/helpers";
import Youtube from "@/components/global-components/Youtube";
import { motion } from "framer-motion";

export interface ColumnsProps {
  blockId: string;
  backgroundColor: ColorOptions;
  backgroundType: "card" | "fill";
  columns: {
    type: "title" | "text" | "image" | "list" | "video";
    titleLarge: string;
    title: string;
    titleParts: IWPTitleParts;
    titlePartsLarge: IWPTitleParts;
    textTitle: string;
    text: string;
    link: {
      color: "red" | "white";
      link: IWPLink;
    };
    secondaryLink: {
      color: "red" | "white";
      link: IWPLink;
    };
    video: IWPVideo;
    image: {
      image: IWPImage;
      imageText?: string;
      textBackground: "fill" | "gradient";
    };
    list: {
      listItem: string;
    }[];
  }[];
}

const Columns: React.FC<ColumnsProps> = ({
  blockId = "",
  backgroundColor,
  backgroundType,
  columns,
}) => {
  const textColor =
    backgroundColor === "yellow"
      ? "brown"
      : isLight(backgroundColor)
        ? "black"
        : "creme";
  const linkColor = (color: "white" | "red") =>
    color === "red" && backgroundColor !== "red" ? "red" : "white";

  const [includesMedia, setIncludesMedia] = React.useState(false);

  useEffect(() => {
    columns.forEach((column) => {
      if (column.type === "image" || column.type === "video") {
        setIncludesMedia(true);
      }
    });
  }, []);

  const renderColumns = columns?.map((column, i) => {
    return (
      <Flex $column $center={column.type === "image"} key={i}>
        {column.type === "title" && (
          <Flex $column>
            {column.title && (
              <DisplayHeadingS size={36} color={textColor} margin="0 0 8px">
                {column.title}
              </DisplayHeadingS>
            )}
            {column.titleParts && (
              <DisplayHeadingS size={36} color={textColor} margin="0 0 8px">
                <TitleParts
                  titleParts={column.titleParts}
                  backgroundColor={backgroundColor}
                />
              </DisplayHeadingS>
            )}
            {column.titleLarge && (
              <S.TitleLarge color={textColor}>{column.titleLarge}</S.TitleLarge>
            )}
            {column.titlePartsLarge && (
              <S.TitleLarge color={textColor}>
                <TitleParts
                  titleParts={column.titlePartsLarge}
                  backgroundColor={backgroundColor}
                />
              </S.TitleLarge>
            )}
          </Flex>
        )}
        {column.type === "text" && (
          <Flex $column $height="100%">
            {column.textTitle && (
              <H3 color={textColor} margin="0 0 16px">
                {column.textTitle}
              </H3>
            )}
            {column.titleParts && (
              <H3 color={textColor} margin="0 0 16px">
                <TitleParts
                  titleParts={column.titleParts}
                  backgroundColor={backgroundColor}
                />
              </H3>
            )}
            <Wysiwyg
              html={column.text}
              color={textColor}
              margin={column.link || column.secondaryLink ? "0 0 16px" : ""}
            />
            <Flex $column $margin="auto 0 0 0">
              {column.link.link && (
                <TextButton
                  fullwidth
                  wpLink={column.link.link}
                  color={linkColor(column.link.color)}
                />
              )}
              {column.secondaryLink.link && (
                <TextButton
                  wpLink={column.secondaryLink.link}
                  color={linkColor(column.secondaryLink.color)}
                />
              )}
            </Flex>
          </Flex>
        )}
        {column.type === "image" && (
          <Flex $center>
            <Image
              image={column.image.image}
              imageText={column.image.imageText}
              textBackground={column.image.textBackground}
              width="100%"
              height="100%"
              radius={10}
              objectFit="contain"
            />
          </Flex>
        )}
        {column.type === "video" && <Youtube video={column.video} />}
        {column.type === "list" && (
          <Flex $column $margin="0 16px">
            <ul>
              {column.list.map((listItem, i) => (
                <li key={i}>
                  <Body color={textColor}>{listItem.listItem}</Body>
                </li>
              ))}
            </ul>
          </Flex>
        )}
      </Flex>
    );
  });

  return (
    <S.Section as={motion.section} {...motionAnimation} id={blockId}>
      <S.Wrapper
        $backgroundColor={backgroundColor}
        $backgroundType={backgroundType}
      >
        <S.Inner>
          <S.Main
            $backgroundColor={backgroundColor}
            $backgroundType={backgroundType}
            $textColor={textColor}
          >
            <S.ColumnContainer
              $includesMedia={includesMedia}
              $numberOfColumns={columns.length}
            >
              {renderColumns}
            </S.ColumnContainer>
          </S.Main>
        </S.Inner>
      </S.Wrapper>
    </S.Section>
  );
};

export default Columns;
